const AUTH_QUERY_KEYS = [
  'code',
  'state',
  'openid',
  'userId',
  'registered',
  'unionid',
  'wechatNickname',
  'wechatHeadImage',
  'name',
  'oauthFail',
  'authError',
]

/** workwx host → wap origin（本地跳 prewap，线上按环境前缀拼） */
export function getWapOrigin() {
  const { host, protocol } = window.location
  if (host.includes('localhost')) {
    return 'https://prewap.liangbaba.com'
  }
  const prefix = host.split('.')[0].split('workwx')[0]
  return `${protocol}//${prefix}wap.liangbaba.com`
}

/** 当前页完整 URL，去掉 OAuth / 鉴权回传参数 */
export function getPartnerBackUrl() {
  const url = new URL(window.location.href)
  AUTH_QUERY_KEYS.forEach((k) => url.searchParams.delete(k))
  return url.toString()
}

/** 登录成功后的默认落地页（仅登录页本身用） */
export function getPartnerHomeBackUrl(redirectPath) {
  const path = redirectPath
    ? (redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`)
    : '/partner-customerList'
  try {
    const url = new URL(window.location.origin)
    url.pathname = path
    return url.toString()
  } catch (e) {
    return `${window.location.origin}/partner-customerList`
  }
}

/**
 * 去 wap 授权时的回跳地址：
 * - 在登录页点登录 → 回首页
 * - 在其它友商页被拉去登录 → 回当前页
 */
export function getPartnerOAuthBackUrl(vm) {
  if (vm && vm.$route && vm.$route.name === 'partner') {
    return getPartnerHomeBackUrl(vm.$route.query && vm.$route.query.redirect)
  }
  return getPartnerBackUrl()
}

/** 构造 wap 友商登录页 URL */
export function buildWapPartnerLoginUrl(backUrl) {
  const origin = getWapOrigin()
  const encoded = encodeURIComponent(backUrl || getPartnerHomeBackUrl())
  // 本地 hash：参数放 # 前面，避免 wap 读不到 backUrl
  if (origin.includes('#')) {
    const base = origin.replace(/\/?#\/?$/, '')
    return `${base}/?backUrl=${encoded}&from=workwx#/partner-login`
  }
  return `${origin}/partner-login?backUrl=${encoded}&from=workwx`
}

export function redirectToWapPartnerLogin(backUrl) {
  window.location.replace(buildWapPartnerLoginUrl(backUrl))
}

export function stripPartnerAuthQueryFromUrl() {
  const url = new URL(window.location.href)
  const hasAuthParam = url.searchParams.has('openid')
  AUTH_QUERY_KEYS.forEach((k) => url.searchParams.delete(k))
  if (hasAuthParam) {
    window.history.replaceState({}, '', url.pathname + url.search + url.hash)
  }
  return hasAuthParam
}

export function parsePartnerAuthFromUrl() {
  const url = new URL(window.location.href)
  const openid = url.searchParams.get('openid')
  if (!openid) return null

  const registeredParam = url.searchParams.get('registered')
  let registered
  if (registeredParam === '0' || registeredParam === 'false') {
    registered = false
  } else if (registeredParam === '1' || registeredParam === 'true') {
    registered = true
  }

  const userIdParam = url.searchParams.get('userId')
  const user = {
    openid,
    unionid: url.searchParams.get('unionid') || undefined,
    wechatNickname: url.searchParams.get('wechatNickname') || undefined,
    wechatHeadImage: url.searchParams.get('wechatHeadImage') || undefined,
    name: url.searchParams.get('name') || undefined,
    registered,
  }
  if (userIdParam) {
    const parsed = Number(userIdParam)
    user.userId = Number.isNaN(parsed) ? userIdParam : parsed
  }
  // registered=1 但无 userId 时按未注册处理，避免 OAuth 回跳后反复跳 wap
  if (!user.userId && registered !== false) {
    user.registered = false
  }
  return user
}

export function getWorkwxOriginFromBackUrl(backUrl) {
  try {
    return new URL(backUrl).origin
  } catch (e) {
    return ''
  }
}

/** wap 登录失败时回跳 workwx /partner 兜底页 */
export function buildWorkwxPartnerFailUrl(backUrl, redirectPath) {
  const origin = getWorkwxOriginFromBackUrl(backUrl) || window.location.origin
  const redirect = encodeURIComponent(redirectPath || '/partner-customerList')
  return `${origin}/partner?oauthFail=1&redirect=${redirect}`
}

const PARTNER_OAUTH_BACK_LOCK_KEY = 'partnerOAuthBackLock'

/** Tab 根路径：在这些页按返回时挡住微信授权页；子页返回不拦 */
const PARTNER_ROOT_TAB_PATHS = [
  '/partner',
  '/partner-customerList',
  '/partner-check',
  '/partner-customerEdit',
  '/partner-logList',
  '/partner-register',
]

function isPartnerRootTabPath(pathname) {
  const path = (pathname || '').replace(/\/$/, '') || '/'
  // 带 id 的编辑/详情不算 Tab 根，应允许返回上一页
  if (path.startsWith('/partner-customerEdit/') && path !== '/partner-customerEdit') {
    return false
  }
  if (path.startsWith('/partner-logDetail')) {
    return false
  }
  return PARTNER_ROOT_TAB_PATHS.includes(path)
}

/** OAuth 成功后标记：阻止回退到微信「自动登录中」页 */
export function markPartnerOAuthBackLock() {
  sessionStorage.setItem(PARTNER_OAUTH_BACK_LOCK_KEY, '1')
}

export function hasPartnerOAuthBackLock() {
  return sessionStorage.getItem(PARTNER_OAUTH_BACK_LOCK_KEY) === '1'
}

export function clearPartnerOAuthBackLock() {
  sessionStorage.removeItem(PARTNER_OAUTH_BACK_LOCK_KEY)
  if (typeof window === 'undefined') return
  if (window.__partnerOAuthBackLockTrap) {
    window.removeEventListener('popstate', window.__partnerOAuthBackLockTrap)
    window.__partnerOAuthBackLockTrap = null
  }
  window.__partnerOAuthBackLockInstalled = false
}

/**
 * 仅在 Tab 根页吞掉「退回微信授权页」；子页（详情/编辑带 id）正常返回。
 * 微信授权中间页在跨域历史里删不掉，只能在本域顶一层哨兵。
 */
export function installPartnerOAuthBackLock() {
  if (typeof window === 'undefined') return
  if (!hasPartnerOAuthBackLock()) return

  const armSentinel = () => {
    if (!hasPartnerOAuthBackLock()) return
    if (!isPartnerRootTabPath(window.location.pathname)) return
    const st = window.history.state
    if (st && st.partnerOAuthLock) return
    try {
      history.pushState({ partnerOAuthLock: 1 }, '', window.location.href)
    } catch (e) {
      // ignore
    }
  }

  const onPopState = () => {
    if (!hasPartnerOAuthBackLock()) return
    // 子页 → 列表 等应用内返回：当前已不在根哨兵场景时不拦
    // 落在 Tab 根上时再顶一层，避免下一跳进微信
    if (!isPartnerRootTabPath(window.location.pathname)) return
    armSentinel()
  }

  if (!window.__partnerOAuthBackLockInstalled) {
    window.__partnerOAuthBackLockInstalled = true
    window.__partnerOAuthBackLockTrap = onPopState
    window.addEventListener('popstate', onPopState)
  }

  // 只压一层，等路由就绪后再补一次（避免被 Vue Router 初始 replaceState 冲掉）
  armSentinel()
  setTimeout(armSentinel, 300)
}

const PARTNER_ROUTE_PATH = {
  partner: '/partner',
  'partner-register': '/partner-register',
  'partner-customerList': '/partner-customerList',
  'partner-check': '/partner-check',
  'partner-customerEdit': '/partner-customerEdit',
  'partner-logList': '/partner-logList',
}

export function getPartnerPathByRouteName(routeName) {
  return PARTNER_ROUTE_PATH[routeName] || '/partner-customerList'
}

/**
 * 用 location.replace 落到干净业务页（去掉 openid 等回跳参数）。
 * 返回 true 表示已触发整页跳转，调用方应停止后续逻辑。
 */
export function replaceToPartnerPath(routeName) {
  const path = getPartnerPathByRouteName(routeName)
  const target = `${window.location.origin}${path}`
  const { pathname, search, hash } = window.location
  if (pathname === path && !search && !hash) return false
  window.location.replace(target)
  return true
}
